import type { Locale } from "@/lib/i18n";

/**
 * UI chrome strings for the public pages — nav, footer, page headers, the search box.
 * Long-form content lives in content/{faq,tutorials,legal}*.ts.
 *
 * ⚠️ HAITIAN CREOLE IS WRITTEN IN THE OFFICIAL 1979 IPN ORTHOGRAPHY, the standard the Akademi
 * Kreyòl Ayisyen regulates. Owner 2026-08-15: "use academic kreyol ortograph". The rules that
 * actually bite when writing it, all verified rather than recalled:
 *
 *   · 32 graphemes. No `c`, `q`, `u` or `x` on their own — `c` and `u` appear only inside the
 *     multigraphs ch, ou, oun, ui.
 *   · The ONLY accent is the grave, on `è` and `ò` (and `à` before n to force an oral reading).
 *     No acute, no circumflex — French spellings are wrong here, so it is `kesyon`, not `question`.
 *   · NO APOSTROPHES AND NO HYPHENS. They were removed by the reform. Write `m ap`, `n ap`,
 *     `l ap` — never `m'ap`. This is the single most common way text betrays itself as unedited.
 *   · Nasal vowels are the digraphs an / en / on.
 *   · The definite article FOLLOWS the noun and its form depends on the preceding sound:
 *     la / lan / a / an / nan. `kont lan`, `kle a`, `machin nan`.
 *
 * TRADING AND CRYPTO TERMS STAY IN ENGLISH and are glossed on first use — stop-loss, take-profit,
 * API, USDC. Kreyòl has no settled vocabulary for them, and inventing one would leave a reader
 * unable to match the word against what their exchange actually shows them on screen.

 */

export type UiKey =
  | "nav.tutorials"
  | "nav.faq"
  | "nav.backToTop"
  | "nav.signIn"
  | "nav.getStarted"
  | "nav.home"
  | "lang.label"
  | "faq.eyebrow"
  | "faq.title"
  | "faq.intro"
  | "faq.introLink"
  | "faq.contents"
  | "faq.reviewed"
  | "faq.questions"
  | "tut.eyebrow"
  | "tut.title"
  | "tut.intro"
  | "tut.introLink"
  | "tut.contents"
  | "tut.before"
  | "tut.note"
  | "tut.guides"
  | "tut.steps"
  | "tut.minutes"
  | "search.placeholder"
  | "search.aria"
  | "search.hintPre"
  | "search.hintMid"
  | "search.hintPost"
  | "search.none"
  | "search.shown"
  | "search.clear"
  | "nav.top"
  | "stuck.title"
  | "stuck.body"
  | "foot.tagline"
  | "foot.talk"
  | "foot.whatsapp"
  | "foot.neverSend"
  | "foot.follow"
  | "foot.product"
  | "foot.learn"
  | "foot.trust"
  | "foot.howItWorks"
  | "foot.pricing"
  | "foot.terms"
  | "foot.privacy"
  | "foot.createAccount"
  | "foot.riskTitle"
  | "foot.risk"
  | "foot.rights"
  | "legal.eyebrow"
  | "legal.noticeTitle"
  | "legal.noticeBody"
  | "legal.version"
  | "legal.updated"
  | "legal.contents";

type Dict = Record<UiKey, string>;

const en: Dict = {
  "nav.tutorials": "Tutorials",
  "nav.faq": "FAQ",
  "nav.backToTop": "Back to top",
  "nav.signIn": "Sign in",
  "nav.getStarted": "Get started",
  "nav.home": "Home",
  "lang.label": "Language",

  "faq.eyebrow": "FAQ",
  "faq.title": "How twoke copy trading works",
  "faq.intro":
    "Straight answers about connecting an exchange key, subscribing to a master, how a trade is sized to your account, and the risk controls you hold. If you would rather be walked through it step by step, start with the",
  "faq.introLink": "tutorials",
  "faq.contents": "Contents",
  "faq.reviewed": "Last reviewed",
  "faq.questions": "questions",

  "tut.eyebrow": "Tutorials",
  "tut.title": "Set up twoke, step by step",
  "tut.intro":
    "Five guides, in order, from creating an account to placing your first copied trade. The second and third cannot be swapped: a subscription is bought for one specific exchange account, so the account has to exist before it can be paid for. For one-off questions, see the",
  "tut.introLink": "FAQ",
  "tut.contents": "Contents",
  "tut.before": "Before you start",
  "tut.note": "Note.",
  "tut.guides": "guides",
  "tut.steps": "steps",
  "tut.minutes": "min",

  "search.placeholder": "Search",
  "search.aria": "Search the page",
  "search.hintPre": "Press",
  "search.hintMid": "to search ·",
  "search.hintPost": "to clear",
  "search.none": "Nothing matches",
  "search.shown": "shown",
  "search.clear": "clear",
  "nav.top": "Back to top",

  "stuck.title": "Still stuck?",
  "stuck.body":
    "with your account email and, if it is about a specific trade, the master and the approximate time. Do not send an API secret — support never needs one, and twoke never asks for it.",

  "foot.tagline":
    "Non-custodial copy trading on Bybit, Binance and cTrader. Your keys, your funds, your risk limits — with a mandatory stop-loss and take-profit on every copied position.",
  "foot.talk": "Talk to us",
  "foot.whatsapp": "Message us on WhatsApp",
  "foot.neverSend":
    "Never send an API secret or a wallet seed phrase — support never asks for either.",
  "foot.follow": "Follow us",
  "foot.product": "Product",
  "foot.learn": "Learn",
  "foot.trust": "Trust & safety",
  "foot.howItWorks": "How it works",
  "foot.pricing": "Pricing",
  "foot.terms": "Terms of Service",
  "foot.privacy": "Privacy Policy",
  "foot.createAccount": "Create an account",
  "foot.riskTitle": "Risk warning.",
  "foot.risk":
    "Trading crypto perpetual futures with leverage carries a substantial risk of loss and is not suitable for everyone. You can lose some or all of the crypto in the account you connect. Copy trading mirrors an automated strategy's decisions into your own exchange account — it is execution software, not financial advice, and past performance does not indicate future results. A stop-loss reduces risk but does not remove it: gaps, slippage and liquidation can still cause losses. Subscriptions are non-refundable. You are responsible for complying with the laws of your jurisdiction.",
  "foot.rights": "non-custodial copy-trading · not financial advice",

  "legal.eyebrow": "Legal",
  "legal.noticeTitle": "Governed by Haitian law",
  "legal.noticeBody":
    "This document describes how twoke operates today: every clause was written against the platform's actual behaviour rather than adapted from a template. twoke is established in the Republic of Haiti and this document is governed by Haitian law. It exists in English and in Haitian Kreyòl; the English version is the reference version.",
  "legal.version": "Version",
  "legal.updated": "last updated",
  "legal.contents": "Contents",
};

const ht: Dict = {
  "nav.tutorials": "Titoryèl",
  "nav.faq": "Kesyon",
  "nav.backToTop": "Retounen anwo",
  "nav.signIn": "Konekte",
  "nav.getStarted": "Kòmanse",
  "nav.home": "Akèy",
  "lang.label": "Lang",

  "faq.eyebrow": "Kesyon yo poze souvan",
  "faq.title": "Kijan copy trading twoke a mache",
  "faq.intro":
    "Repons dirèk sou kijan pou konekte yon kle echanj, kijan pou abòne ak yon mèt, kijan yo kalkile gwosè yon trade sou kont pa w, ak kontwòl risk ki nan men w. Si ou pito yon gid etap pa etap, kòmanse ak",
  "faq.introLink": "titoryèl yo",
  "faq.contents": "Sa ki ladan",
  "faq.reviewed": "Dènye revizyon",
  "faq.questions": "kesyon",

  "tut.eyebrow": "Titoryèl",
  "tut.title": "Konfigire twoke, etap pa etap",
  "tut.intro":
    "Senk gid, nan lòd, depi kreye yon kont jouk ou plase premye trade kopye w la. Ou pa ka chanje lòd dezyèm ak twazyèm nan: yo achte yon abònman pou yon sèl kont echanj presi, donk kont lan dwe egziste anvan ou ka peye pou li. Pou kesyon rapid, gade",
  "tut.introLink": "paj kesyon yo",
  "tut.contents": "Sa ki ladan",
  "tut.before": "Anvan ou kòmanse",
  "tut.note": "Remak.",
  "tut.guides": "gid",
  "tut.steps": "etap",
  "tut.minutes": "min",

  "search.placeholder": "Chèche",
  "search.aria": "Chèche nan paj la",
  "search.hintPre": "Peze",
  "search.hintMid": "pou chèche ·",
  "search.hintPost": "pou efase",
  "search.none": "Anyen pa koresponn ak",
  "search.shown": "parèt",
  "search.clear": "efase",
  "nav.top": "Retounen anwo",

  "stuck.title": "Ou bloke toujou?",
  "stuck.body":
    "ak imel kont ou, epi si se sou yon trade presi, di ki mèt ak apeprè ki lè. Pa janm voye yon sekrè API — sipò pa janm bezwen li, epi twoke pa janm mande l.",

  "foot.tagline":
    "Copy trading sou pwòp kont Bybit, Binance, MEXC ak cTrader ou. Kle pa w, kripto pa w, limit risk pa w — ak yon stop-loss epi yon take-profit obligatwa sou chak pozisyon kopye.",
  "foot.talk": "Pale ak nou",
  "foot.whatsapp": "Ekri nou sou WhatsApp",
  "foot.neverSend":
    "Pa janm voye yon sekrè API ni yon fraz sekrè pòtfèy — sipò pa janm mande yo.",
  "foot.follow": "Swiv nou",
  "foot.product": "Pwodwi",
  "foot.learn": "Aprann",
  "foot.trust": "Konfyans ak sekirite",
  "foot.howItWorks": "Kijan li mache",
  "foot.pricing": "Pri",
  "foot.terms": "Kondisyon Sèvis",
  "foot.privacy": "Règleman Konfidansyalite",
  "foot.createAccount": "Kreye yon kont",
  "foot.riskTitle": "Avètisman sou risk.",
  "foot.risk":
    "Fè trading sou kripto perpetual futures ak leverage pote yon gwo risk pou ou pèdi kripto epi li pa bon pou tout moun. Ou ka pèdi yon pati oswa tout kripto ki nan kont ou konekte a. Copy trading kopye desizyon yon estrateji otomatik nan pwòp kont echanj ou — se yon lojisyèl egzekisyon, se pa konsèy finansye, epi rezilta ki pase pa endike rezilta k ap vini. Yon stop-loss diminye risk men li pa retire l: gap, slippage ak likidasyon ka toujou lakòz pèt. Abònman pa ranbousab. Se responsablite w pou w respekte lwa peyi w.",
  "foot.rights": "copy trading · se pa konsèy finansye",

  "legal.eyebrow": "Legal",
  "legal.noticeTitle": "Lwa ayisyen gouvènen l",
  "legal.noticeBody":
    "Dokiman sa a dekri kijan twoke fonksyone jodi a: yo ekri chak atik dapre sa platfòm nan fè vre, se pa yon modèl yo kopye. twoke etabli nan Repiblik Dayiti epi se lwa ayisyen ki gouvènen dokiman sa a. Li egziste an anglè ak an kreyòl ayisyen; vèsyon anglè a se vèsyon referans lan.",
  "legal.version": "Vèsyon",
  "legal.updated": "dènye mizajou",
  "legal.contents": "Sa ki ladan",
};

const DICTS: Record<Locale, Dict> = { en, ht };

/** Look up a UI string. Falls back to English for a key a locale has not translated — a missing
 *  chrome label should degrade to English, never to a blank or to the raw key. */
export const t = (locale: Locale, key: UiKey): string =>
  DICTS[locale]?.[key] ?? en[key];
